import * as React from 'react';
import type { PackageOfferingBreakdown } from 'app/shared/model/EditPricelist';
import type { BreakdownVM } from '../domain/packageBreakdowns/types';
import {
  buildBreakdownVMs,
  buildBreakdownColorMap,
  formatPercent,
} from '../domain/packageBreakdowns/helpers';
import { productTypeNameFromId } from '../domain/offering/productType';

export interface UseBreakdownsModalParams {
  breakdownNameMap: Map<string, string>;
}

export interface UseBreakdownsModalResult {
  isOpen: boolean;
  title: string;
  items: BreakdownVM[];
  colors: Map<string, string>;
  total: number;
  hoverKey: string | null;
  setHoverKey: (key: string | null) => void;
  tooltipByKey: Map<string, string>;
  open: (product: { name?: string; packageOfferingBreakdowns?: PackageOfferingBreakdown[] }) => void;
  close: () => void;
}

export function useBreakdownsModal(
  params: UseBreakdownsModalParams
): UseBreakdownsModalResult {
  const { breakdownNameMap } = params;

  const [isOpen, setIsOpen] = React.useState(false);
  const [title, setTitle] = React.useState<string>('Breakdowns');
  const [rawBreakdowns, setRawBreakdowns] = React.useState<PackageOfferingBreakdown[]>([]);
  const [hoverKey, setHoverKey] = React.useState<string | null>(null);

  const items = React.useMemo(
    () => buildBreakdownVMs(rawBreakdowns, breakdownNameMap, productTypeNameFromId),
    [rawBreakdowns, breakdownNameMap]
  );

  const colors = React.useMemo(() => buildBreakdownColorMap(items), [items]);

  const total = React.useMemo(
    () => items.reduce((s, x) => s + (x.percentage ?? 0), 0),
    [items]
  );

  const tooltipByKey = React.useMemo(() => {
    const m = new Map<string, string>();
    items.forEach((x) => {
      m.set(x.key, `${x.typeName}: ${x.productName} — ${formatPercent(x.percentage)}`);
    });
    return m;
  }, [items]);

  const open = React.useCallback(
    (product: { name?: string; packageOfferingBreakdowns?: PackageOfferingBreakdown[] }) => {
      const breakdowns = (product.packageOfferingBreakdowns ?? []) as PackageOfferingBreakdown[];
      setHoverKey(null);
      setRawBreakdowns(breakdowns);
      setTitle(`Breakdowns for '${String(product.name ?? '')}'`);
      setIsOpen(true);
    },
    []
  );

  const close = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    title,
    items,
    colors,
    total,
    hoverKey,
    setHoverKey,
    tooltipByKey,
    open,
    close,
  };
}

